import { DataView } from 'primereact/dataview';
import { Dispatch, FC, RefObject, SetStateAction, useEffect, useState } from 'react';
import { SkillAncestries } from '../../models/SkillAncestries';
import { Skill } from '../../models/Skill';
import { sortSkillsByDifficulty } from '../../businessLogicService/sortCriteria';
import SingleUnitSkillDialog from './SingleUnitSkillDialog';
import SingleUnitProgressBar from './SingleUnitProgressBar';
import { progressBarSingleUnit } from '../../businessLogicService/progressBarAssignValues';
import { ProgressBar, ProgressBarParent } from '../../models/ProgressBar';
import { UsersBySkillResults } from '../../models/UsersBySkillResults';
import { Button } from 'primereact/button';
import { Tooltip } from 'primereact/tooltip';
import { TieredMenu } from 'primereact/tieredmenu';
import { MenuItem } from 'primereact/menuitem';
import SingleUnitStandardBadges from './SingleUnitStandardBadges';
import { AssignmentOpenModalButton } from '../Assignments/AssignmentOpenModalButton';
import { SelectAssignmentModeModal } from '../../models/Assignment';
import { TargetSkillTypeLabel } from '../../models/TargetSkillType';

interface Props {
  arrayRelevance: SkillAncestries[];
  nameRelevance: string;
  skillTypeUnit: Skill[];
  tieredMenuRef: RefObject<TieredMenu>;
  setItemsTieredMenu: (i: MenuItem[]) => void;
  targetSkillsIds: string[];
  selectedStandardIds?: string[];
  setCreateAssignmentModal: Dispatch<SetStateAction<SelectAssignmentModeModal>>;
}

const SingleUnitSupportSkillsCard: FC<Props> = ({
  skillTypeUnit,
  arrayRelevance,
  nameRelevance,
  tieredMenuRef,
  setItemsTieredMenu,
  targetSkillsIds,
  selectedStandardIds,
  setCreateAssignmentModal,
}) => {
  const [visible, setVisible] = useState<boolean>(false);
  const [skill, setSkill] = useState<Skill>();
  const [isSkillsExistInTargetSkill, setIsSkillsExistInTargetSkill] = useState<boolean>(false);

  useEffect(() => {
    //check if there is at least one ancestorSkill matching with targetSkillsIds, if not show an empty message
    const ancestorSkillIds = arrayRelevance.map(el => el.ancestorSkill.id);
    setIsSkillsExistInTargetSkill(targetSkillsIds.some(id => ancestorSkillIds.includes(id)));
  }, []);

  const supportSkillTemplate = (skill: Skill) => {
    const skillExistInTargetSkill = targetSkillsIds.includes(skill.id);
    const matchWithTarget = skillTypeUnit.map(el => el.id).includes(skill.id);
    let values: ProgressBar[] | null = null;
    if (skill.usersBySkillResults) {
      values = progressBarSingleUnit(skill.usersBySkillResults as UsersBySkillResults[]);
    }

    return (
      skillExistInTargetSkill && (
        <div className="nc_support-skill-item flex align-items-center">
          <div className="nc_support-skill-content flex flex-column flex-grow-1 gap-sm">
            <div className="flex align-items-center gap-md">
              <Button
                link
                onClick={() => {
                  setSkill(skill);
                  setVisible(true);
                }}
              >
                {skill.name} ({skill.code})
              </Button>
              {matchWithTarget && (
                <>
                  <Tooltip target={`.icon-code-${skill.code}`} />
                  <i
                    className={`nc_icon-nc_target icon-sm icon-code-${skill.code} `}
                    data-pr-tooltip="This is also a Target Skill for this Unit"
                  />
                </>
              )}
              {skill.lessonNumbers?.length ? (
                <h6 className="nc_unit-lessons">Lessons: {skill.lessonNumbers.map(el => el).join(', ')}</h6>
              ) : (
                <></>
              )}
            </div>
            <SingleUnitStandardBadges standards={skill.standards} selectedStandardIds={selectedStandardIds} />
          </div>
          {skill.usersBySkillResults && (
            <SingleUnitProgressBar
              values={values as ProgressBar[]}
              parent={ProgressBarParent.SINGLE_UNIT}
              tieredMenuRef={tieredMenuRef}
              setItemsTieredMenu={setItemsTieredMenu}
            />
          )}
          <AssignmentOpenModalButton
            setCreateAssignmentModal={setCreateAssignmentModal}
            entryPoint="units"
            skill={{
              id: skill.id,
              code: skill.code,
              name: skill.name,
              // The button defaults to Target; an ancestor only counts as one when the unit also targets it.
              targetSkillType: matchWithTarget ? TargetSkillTypeLabel.TARGET : TargetSkillTypeLabel.SUPPORT,
            }}
            customHeight={true}
          />
        </div>
      )
    );
  };

  return (
    <>
      <div className="lg:w-10 w-full nc_support-skills nc_support-skills-teacher">
        <h4 className="flex align-items-center gap-md">
          <i className="nc_icon-nc_support icon-sm nc_unit-skill-type-icon" />
          {nameRelevance} Skill&#40;s&#41;
        </h4>
        <DataView
          value={
            isSkillsExistInTargetSkill
              ? arrayRelevance
                  .map(element => {
                    return element.ancestorSkill;
                  })
                  .sort(sortSkillsByDifficulty)
              : []
          }
          itemTemplate={supportSkillTemplate}
        ></DataView>
      </div>
      {visible && skill && <SingleUnitSkillDialog visible={visible} setVisible={setVisible} skill={skill} />}
    </>
  );
};

export default SingleUnitSupportSkillsCard;
